import Grid from "@mui/material/Grid";
import {
    Autocomplete,
    Checkbox,
    FormControlLabel,
    FormHelperText,
    InputLabel,
    TextField
} from "@mui/material";
import Button from "@mui/material/Button";
import {Save} from "@mui/icons-material";
import * as React from "react";
import helpers from "../../helpers/common";
import useRootHook from "../../hooks/useRoot";
import {useEffect, useState} from "react";
import merchants from "../../models/merchants";

export default function ProjectForm ({project, fields, setFields, errors, setErrors, handleChange, handleBlur, handleSubmit, readOnly = false}) {
    const {userData} = useRootHook()
    const [merchantsList, setMerchantList] = useState(null)
    useEffect(() => {
        userData.role !== helpers.roles.M && merchants.list()
            .then((data) => {
                setMerchantList(data)
            })
    }, [])

    if (userData.role !== helpers.roles.M && !merchantsList) return null

    return <React.Fragment>
        <Grid container spacing={2}
              component="form"
              onSubmit={handleSubmit}
              autoComplete="off"
        >
            {userData.role !== helpers.roles.M && !project && <Grid size={12}>
                <InputLabel>Merchant</InputLabel>
                <Autocomplete
                    disablePortal
                    id="merchant_id"
                    name="merchant_id"
                    options={merchantsList || []}
                    getOptionLabel={(option) => option.name || option.username || ''}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    value={merchantsList?.find(m => m.id === fields.merchant_id) || null}
                    fullWidth={true}
                    required={true}
                    onChange={(_, newValue) => {
                        setErrors({...errors, merchant_id: undefined});
                        setFields((prevFields) => {return {...prevFields, merchant_id: newValue ? newValue.id : null}})
                    }}
                    onBlur={() => {
                        if (!fields.merchant_id) setErrors((prevErrors) => {return {...prevErrors, merchant_id: 'No merchant selected'}})
                    }}
                    renderInput={(params) => <TextField {...params} error={!!errors.merchant_id} label="Select merchant"/>}
                />
                {errors.merchant_id && <FormHelperText>{errors.merchant_id}</FormHelperText>}
            </Grid>}
            <Grid size={{ xs: 12, md: 6 }}>
                <InputLabel>Name</InputLabel>
                <TextField
                    error={!!errors.name}
                    value={fields.name || ""}
                    name="name"
                    id="name"
                    required={true}
                    fullWidth={true}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    disabled={readOnly}
                    InputProps={{
                        readOnly: readOnly
                    }}
                />
                {errors.name && <FormHelperText>{errors.name}</FormHelperText>}
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
                <InputLabel>Email</InputLabel>
                <TextField
                    error={!!errors.email}
                    value={fields.email || ""}
                    name="email"
                    id="email"
                    required={true}
                    fullWidth={true}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    disabled={readOnly}
                    InputProps={{
                        readOnly: readOnly
                    }}
                />
                {errors.email && <FormHelperText>{errors.email}</FormHelperText>}
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
                <InputLabel>Phone</InputLabel>
                <TextField
                    error={!!errors.phone}
                    value={fields.phone || ""}
                    name="phone"
                    id="phone"
                    required={true}
                    fullWidth={true}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    disabled={readOnly}
                    InputProps={{
                        readOnly: readOnly
                    }}
                />
                {errors.phone && <FormHelperText>{errors.phone}</FormHelperText>}
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
                <InputLabel>URL</InputLabel>
                <TextField
                    disabled={readOnly || (project && project.url && userData.role === helpers.roles.M)}
                    error={!!errors.url}
                    value={fields.url || ""}
                    name="url"
                    id="url"
                    required={true}
                    fullWidth={true}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    InputProps={{
                        readOnly: readOnly
                    }}
                />
                {errors.url && <FormHelperText>{errors.url}</FormHelperText>}
            </Grid>
            <Grid size={12}>
                {<FormControlLabel
                    control={
                        <Checkbox
                            name="enabled"
                            disabled={readOnly || userData.role === helpers.roles.M}
                            checked={typeof fields.enabled === 'boolean' ? fields.enabled : false}
                            onChange={handleChange}
                            value={typeof fields.enabled === 'boolean' ? fields.enabled : false}
                            color="secondary"
                        />
                    }
                    label="Enabled"
                />}
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
            <InputLabel>Global Fee %</InputLabel>
            <TextField
                name="fee_global_pct"
                value={fields.fee_global_pct ?? ''}
                onChange={handleChange}
                fullWidth
                disabled={readOnly}
                InputProps={{
                    readOnly: readOnly
                }}
            />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
                <InputLabel>Dispute Cost (project global)</InputLabel>
                <TextField
                    name="pm_dispute_cost"
                    value={fields.pm_dispute_cost ?? ''}
                    onChange={handleChange}
                    fullWidth
                    disabled={readOnly}
                    InputProps={{
                        readOnly: readOnly
                    }}
                />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
                <InputLabel>Hold days (project global)</InputLabel>
                <TextField
                    name="pm_hold_days"
                    value={fields.pm_hold_days ?? 0}
                    onChange={handleChange}
                    fullWidth
                    disabled={readOnly}
                    InputProps={{
                        readOnly: readOnly
                    }}
                />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
                <InputLabel>Hold amount % (project global)</InputLabel>
                <TextField
                    name="pm_hold_amount"
                    value={fields.pm_hold_amount ?? 0}
                    onChange={handleChange}
                    fullWidth
                    disabled={readOnly}
                    InputProps={{
                        readOnly: readOnly
                    }}
                />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
                <InputLabel>Rolling reserve days (project global)</InputLabel>
                <TextField
                    name="pm_reserve_days"
                    value={fields.pm_reserve_days ?? 0}
                    onChange={handleChange}
                    fullWidth
                    disabled={readOnly}
                    InputProps={{
                        readOnly: readOnly
                    }}
                />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
                <InputLabel>Rolling reserve amount % (project global)</InputLabel>
                <TextField
                    name="pm_reserve_amount"
                    value={fields.pm_reserve_amount ?? 0}
                    onChange={handleChange}
                    fullWidth
                    disabled={readOnly}
                    InputProps={{
                        readOnly: readOnly
                    }}
                />
            </Grid>
            {!readOnly && (
                <Grid container size={12} spacing={3} sx={{mt: 2}}>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <Button type="submit" onClick={handleSubmit} variant="outlined" startIcon={<Save />} >
                            Save
                        </Button>
                    </Grid>
                </Grid>
            )}
        </Grid>
    </React.Fragment>
}
